import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy,
    HostListener
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TabCommunicationService} from '@services/tab-communication.service';
import {HouseDetailsWidgetComponent} from '../house-details-widget/house-details-widget.component';
import {ProgressBarModule} from 'primeng/progressbar';
import {
    DashboardKeyMetricsService,
    DashboardFarmHouseOverviewResponse
} from '@services/dashboard-key-metrics.service';
import {ToastrService} from 'ngx-toastr';
import {formatToTwoDecimalPlaces} from '@/utils/helpers';
import {interval, Subscription} from 'rxjs';
import {CameraDetail} from '@/common/interface/camera-detail.model';
@Component({
    selector: 'app-insights-panel',
    standalone: true,
    imports: [CommonModule, HouseDetailsWidgetComponent, ProgressBarModule],
    templateUrl: './insights-panel.component.html',
    styleUrl: './insights-panel.component.scss'
})
export class InsightsPanelComponent implements OnInit, OnDestroy {
    activeTab: string = 'overview';
    @Input() placement: any;
    @Input() placementId: number;
    @Input() medications: any;
    @Input() averageWeights: any;
    @Input() houseId: number | null = null;
    @Output() tabChanged = new EventEmitter<string>();
    formatToTwoDecimalPlaces = formatToTwoDecimalPlaces;
    // Data from API
    houseData: DashboardFarmHouseOverviewResponse | null = null;
    cameraDetails: CameraDetail[] = [];
    loading: boolean = false;
    cameraLoading: boolean = false;
    // Track expanded state per sensor list key
    private expandedKeys = new Set<string>();
    // Auto-refresh subscription
    private cameraRefreshSubscription?: Subscription;
    // Set up interval to refresh every 15 minutes (900000 ms)
    readonly cameraRefreshInterval = 900000;
    // Lightbox state
    lightboxOpen: boolean = false;
    currentCameraIndex: number = 0;
    constructor(
        private tabCommunicationService: TabCommunicationService,
        private dashboardService: DashboardKeyMetricsService,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        this.tabCommunicationService.setActiveTab(this.activeTab);
        this.loadHouseData();
        this.startCameraAutoRefresh();
    }

    ngOnDestroy(): void {
        this.stopCameraAutoRefresh();
    }

    private loadHouseData(): void {
        if (!this.houseId) {
            return;
        }

        const userDetail = JSON.parse(
            localStorage.getItem('UserDetail') || '{}'
        );
        const userId = userDetail?.id;

        if (!userId) {
            this.toastr.error('User not found');
            return;
        }

        this.loading = true;
        this.dashboardService
            .getDashboardFarmHouseOverview(userId, this.houseId)
            .subscribe({
                next: (data) => {
                    this.houseData = data;
                    this.loading = false;
                },
                error: (error) => {
                    console.error('Error loading house data:', error);
                    this.toastr.error('Failed to load house data');
                    this.loading = false;
                }
            });
    }

    private loadCameraDetails(): void {
        if (!this.houseId) {
            return;
        }

        this.cameraLoading = true;
        this.dashboardService
            .getCameraDetailsOfFarmhouse(this.houseId)
            .subscribe({
                next: (data) => {
                    this.cameraDetails = data;
                    this.cameraLoading = false;
                },
                error: (error) => {
                    console.error('Error loading camera details:', error);
                    this.toastr.error('Failed to load camera details');
                    this.cameraLoading = false;
                }
            });
    }

    private startCameraAutoRefresh(): void {
        this.loadCameraDetails();

        this.cameraRefreshSubscription = interval(
            this.cameraRefreshInterval
        ).subscribe(() => {
            this.loadCameraDetails();
        });
    }

    private stopCameraAutoRefresh(): void {
        if (this.cameraRefreshSubscription) {
            this.cameraRefreshSubscription.unsubscribe();
        }
    }

    @Input() mortalities: any;
    @Input() vaccinations: any;
    @Input() bodyWeights: any;
    @Input() timeline: any;
    @Input() performance: any;

    tabs = [{id: 'overview', label: 'General Overview'}];

    setActiveTab(tabId: string): void {
        this.activeTab = tabId;
        this.tabChanged.emit(tabId);
        this.tabCommunicationService.setActiveTab(tabId);
    }

    isActiveTab(tabId: string): boolean {
        return this.activeTab === tabId;
    }

    onBack(): void {
        if (typeof window !== 'undefined' && window.history.length > 1) {
            window.history.back();
        }
    }

    // Helper methods for template
    get weightProgress(): number {
        if (!this.houseData?.weightProgress) return 0;
        const current = this.houseData.weightProgress.currentWeight || 0;
        const target = this.houseData.weightProgress.targetWeight || 0;
        return target > 0 ? Math.round((current / target) * 100) : 0;
    }

    getStatusClass(value: number, min: number, max: number): string {
        if (value >= min && value <= max) {
            return 'status-normal';
        } else if (value < min * 0.8 || value > max * 1.2) {
            return 'status-critical';
        } else {
            return 'status-warning';
        }
    }

    getCameraStatusClass(status: string): string {
        if (!status) return 'status-normal';

        const statusLower = status.toLowerCase();
        if (statusLower.includes('normal')) {
            return 'status-normal';
        } else if (statusLower.includes('critical')) {
            return 'status-critical';
        } else if (statusLower.includes('warning')) {
            return 'status-warning';
        }
        return 'status-normal';
    }

    getCurrentDateTime(): string {
        return new Date().toLocaleString();
    }

    getTemperatureSensors() {
        const tempSensor = this.houseData?.environmentalSensors?.find(
            (s) => s.sensorType === 'Temperature'
        );
        return tempSensor?.sensors || [];
    }

    getHumiditySensors() {
        const humiditySensor = this.houseData?.environmentalSensors?.find(
            (s) => s.sensorType === 'Humidity'
        );
        return humiditySensor?.sensors || [];
    }

    getCO2Sensors() {
        const co2Sensor = this.houseData?.environmentalSensors?.find(
            (s) => s.sensorType === 'CO₂'
        );
        return co2Sensor?.sensors || [];
    }

    getLightIntensitySensors() {
        const lightSensor = this.houseData?.environmentalSensors?.find(
            (s) => s.sensorType === 'Light Intencty'
        );
        return lightSensor?.sensors || [];
    }

    getWaterFlowSensors() {
        const waterFlowSensor = this.houseData?.environmentalSensors?.find(
            (s) => s.sensorType === 'Water Flow'
        );
        return waterFlowSensor?.sensors || [];
    }

    // Expandable list helpers
    isExpanded(key: string): boolean {
        return this.expandedKeys.has(key);
    }

    toggleExpand(key: string): void {
        if (this.expandedKeys.has(key)) {
            this.expandedKeys.delete(key);
        } else {
            this.expandedKeys.add(key);
        }
    }

    hasSensorData(): boolean {
        if (!this.houseData?.liveSensors) {
            return false;
        }
        const sensors = this.houseData.liveSensors;
        return (
            (sensors.temperature !== null &&
                sensors.temperature !== undefined) ||
            (sensors.humidity !== null && sensors.humidity !== undefined) ||
            (sensors.cO2 !== null && sensors.cO2 !== undefined) ||
            (sensors.weight !== null && sensors.weight !== undefined) ||
            (sensors.waterFlow !== null && sensors.waterFlow !== undefined) ||
            (sensors.lightIntensity !== null &&
                sensors.lightIntensity !== undefined)
        );
    }

    openLightbox(index: number): void {
        if (index < 0 || index >= this.cameraDetails.length) {
            return;
        }
        this.currentCameraIndex = index;
        this.lightboxOpen = true;
        document.body.style.overflow = 'hidden';
    }

    closeLightbox(): void {
        this.lightboxOpen = false;
        document.body.style.overflow = '';
    }

    navigateLightbox(direction: 'prev' | 'next'): void {
        if (direction === 'prev') {
            this.currentCameraIndex =
                this.currentCameraIndex > 0
                    ? this.currentCameraIndex - 1
                    : this.cameraDetails.length - 1;
        } else {
            this.currentCameraIndex =
                this.currentCameraIndex < this.cameraDetails.length - 1
                    ? this.currentCameraIndex + 1
                    : 0;
        }
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void {
        if (!this.lightboxOpen) return;

        switch (event.key) {
            case 'Escape':
                this.closeLightbox();
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.navigateLightbox('prev');
                break;
            case 'ArrowRight':
                event.preventDefault();
                this.navigateLightbox('next');
                break;
        }
    }

    get currentCamera(): CameraDetail | null {
        return this.cameraDetails[this.currentCameraIndex] || null;
    }
}
